import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useLazyViewSolarPlantStatisticsAdminQuery,
  useLazyViewSolarPlantStatusesQuery,
  useLazyViewTotalPlantGenerationQuery,
} from "../../redux/api/dashboard/dashboardAPI";
import { setSelectPlantStatus } from "../../redux/features/sites/sites-slice";
import SiteStatusCount from "../../shared/components/site-status-count/site-status-count";
import SolarInfoStatisticCard from "../../shared/components/solar-info-statistic-card/solar-info-statistic-card";
import IViewCustomerSolarPlantDetailsResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-customer-solar-plant-details-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { IViewTotalSolarPlantEnergyResponseDTO } from "../../shared/oversight-core/dtos/response-dtos/view-total-solar-plant-energy-response-dto";
import { EPlantStatus } from "../../shared/oversight-core/enums/plant-status";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import BarChart from "../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import Helper from "../../shared/oversight-core/utils/helpers";
import styles from "./dashboard.module.scss";

interface ChartData {
  values: number[];
  labels: string[];
}

const defaultChartDataValues = {
  values: [],
  labels: [],
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState<IViewCustomerSolarPlantDetailsResponseDTO>();
  const [solarPowerData, setSolarPowerData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [solarPlantStatuses, setSolarPlantStatuses] =
    useState<ViewSolarPlantStatusesResponseDTO>();

  const [
    triggerSolarPlantStatistics,
    { isFetching: isFetchingSolarPlantStatistics },
  ] = useLazyViewSolarPlantStatisticsAdminQuery();
  const [
    triggerTotalPlantGeneration,
    { isFetching: isFetchingTotalPlantGeneration },
  ] = useLazyViewTotalPlantGenerationQuery();
  const [
    triggerSolarPlantStatuses,
    { isFetching: isFetchingSolarPlantStatuses },
  ] = useLazyViewSolarPlantStatusesQuery();

  useEffect(() => {
    triggerSolarPlantStatistics()
      .unwrap()
      .then((res: IViewCustomerSolarPlantDetailsResponseDTO) => {
        setData(res);
      })
      .catch(() => {
        setData(undefined);
      });
  }, [triggerSolarPlantStatistics]);

  useEffect(() => {
    triggerSolarPlantStatuses({ extended: false })
      .unwrap()
      .then((res: ViewSolarPlantStatusesResponseDTO) => {
        setSolarPlantStatuses(res);
      })
      .catch(() => {
        setSolarPlantStatuses(undefined);
      });
  }, [triggerSolarPlantStatuses]);

  useEffect(() => {
    triggerTotalPlantGeneration({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    })
      .unwrap()
      .then((res: IViewTotalSolarPlantEnergyResponseDTO) => {
        const power = res.solarGeneration;

        setSolarPowerData({
          values: power.map((p) => p.energy / 1000),
          labels: power.map((p) => `${p.day}`),
        });
      })
      .catch(() => {
        setSolarPowerData({ ...defaultChartDataValues });
      });
  }, [triggerTotalPlantGeneration]);

  const formatGeneration = (
    totalGeneration: number
  ): { value: number; unit: string } => {
    const absValue = Math.abs(totalGeneration);
    const digits = Math.floor(Math.log10(absValue)) + 1;

    if (digits <= 8) {
      // Convert to kW
      return {
        value: Number(Helper.roundTo2(totalGeneration / 1_000)),
        unit: "kWh",
      };
    } else {
      // Convert to MW
      return {
        value: Number(Helper.roundTo2(totalGeneration / 1_000_000)),
        unit: "MWh",
      };
    }
  };

  return (
    <>
      <div className="container-white px-3">
        <Row className="gy-4">
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Total Sites"
              value={data?.numberOfPlant || 0}
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Total Generation"
              value={formatGeneration(data?.totalGeneration ?? 0).value}
              unit={formatGeneration(data?.totalGeneration ?? 0).unit}
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Monthly Total"
              value={Number(
                Helper.roundTo2((data?.totalMonthlyGeneration ?? 0) / 1000)
              )}
              unit="kWh"
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Today Total Generation"
              value={Number(
                Helper.roundTo2((data?.totalDailyGeneration ?? 0) / 1000)
              )}
              unit="kWh"
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Largest Individual Site"
              value={Number(
                Helper.roundTo2((data?.largestSiteCapacity ?? 0) / 1000)
              )}
              unit="kWh"
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
        </Row>
      </div>
      <div className={`${styles.siteStatusCardSContainer} p-2 mt-4`}>
        <Row className="row-cols-1 row-cols-sm-2 row-cols-xl-4 g-2">
          <Col>
            <SiteStatusCount
              title="All Sites"
              count={solarPlantStatuses?.numberOfTotalPlants || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(setSelectPlantStatus(undefined));
              }}
              icon="solar_power"
              iconColor="#469CD9"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
          <Col>
            <SiteStatusCount
              title="Active Sites"
              count={solarPlantStatuses?.numberOfOnlinePlants || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(setSelectPlantStatus(EPlantStatus.GENERATING));
              }}
              icon="fiber_manual_record"
              iconColor="#29CC39"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
          <Col>
            <SiteStatusCount
              title="Offline Sites"
              count={solarPlantStatuses?.numberOfOfflinePlants || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(setSelectPlantStatus(EPlantStatus.OFFLINE));
              }}
              icon="wifi_off"
              iconColor="#7D7B86"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
          <Col>
            <SiteStatusCount
              title="Errors"
              count={solarPlantStatuses?.numberOfAlerts || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(setSelectPlantStatus(EPlantStatus.FAULT));
              }}
              icon="emergency_home"
              iconColor="#E45E5E"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
        </Row>
      </div>
      <div className="container-white position-relative mt-4">
        <BarChart
          data1={solarPowerData.values}
          dataLabel1="Solar Power Generation of All Sites"
          labels={solarPowerData.labels}
          borderColor1="#D08700"
          backgroundColor1="#D08700"
          yAxesUnit="kWh"
          isDisplayLegend
          isChartDataAvailable={solarPowerData.values.length > 0}
        />
        <SpinnerModal show={isFetchingTotalPlantGeneration} />
      </div>
    </>
  );
};

export default AdminDashboard;
